<template>
  <div v-if="!!phaseData && isMounted">
    <template v-for="familyOption in familyAtopiaOptions">
      <radio-options
        :key="familyOption.key"
        :label="familyOption.name + ' na família?'"
        class="mt-4"
        v-model="phaseData.familyAtopias[familyOption.key].selected"
        row
        hide-details
        :options="defaultProfileOptions"
        clearable
        @change="resetObject(familyOption.key)"
        :rules="familyOption.required ? [$validation.radioRequired()] : []"
        :required="familyOption.required"
      />

      <field-label
        :label="'Grau de parentesco:'"
        :key="familyOption.key + '_degree_wrapper'"
        class="mt-3"
        v-if="phaseData.familyAtopias[familyOption.key].selected"
        :required="familyOption.required"
      >
        <div class="d-flex ml-3">
          <div v-for="degree in familyOption.degrees" :key="familyOption.key + '_' + degree + 'grau'" class="mr-3">
            <v-checkbox
              dense
              :label="degree + ' Grau'"
              :value="degree"
              v-model="phaseData.familyAtopias[familyOption.key].degree"
            />
          </div>
        </div>
      </field-label>
    </template>

    <template v-for="profileOption in profileOptions">
      <radio-options
        class="mt-4"
        :key="`profile-${profileOption.key}`"
        :label="profileOption?.name"
        v-model="phaseData.profile[profileOption.key].value"
        row
        clearable
        hide-details
        :options="defaultProfileOptions"
        @change="checkProfile(profileOption.key, $event)"
        :rules="profileOption.required ? [$validation.radioRequired()] : []"
        :required="profileOption.required"
      />
      <text-area
        class="mt-3 ml-3"
        v-if="!!profileOption.complementText"
        v-show="phaseData.profile[profileOption.key].value"
        :key="`profile-extra-${profileOption.key}`"
        :label="profileOption.complementText"
        v-model="phaseData.profile[profileOption.key].complement"
        :max-length="100"
        :rows="1"
        hide-details
        :required="profileOption.required"
      />
      <text-area
        class="mt-3 ml-3"
        v-if="!!profileOption.askForTreatment"
        v-show="phaseData.profile[profileOption.key].value"
        :key="`profile-treatment-${profileOption.key}`"
        label="Quais tratamentos foram feitos?"
        v-model="phaseData.profile[profileOption.key].treatment"
        :max-length="100"
        :rows="1"
        hide-details
        :required="profileOption.required"
      />
      <field-label
        :label="'Grau de parentesco:'"
        :key="profileOption.key + '_degree_wrapper'"
        class="mt-3 ml-3"
        v-if="phaseData.profile[profileOption.key].value"
        :required="profileOption.required"
      >
        <div class="d-flex ml-3">
          <div v-for="degree in profileOption.degrees" :key="profileOption.key + '_' + degree + 'grau'" class="mr-3">
            <v-checkbox
              dense
              :label="degree + ' Grau'"
              :value="degree"
              v-model="phaseData.profile[profileOption.key].degree"
            />
          </div>
        </div>
      </field-label>
    </template>

    <field-label class="mt-8" label="Anotações">
      <text-area :readonly="disabled" :max-length="2000" v-model="phaseData.notes" />
    </field-label>
  </div>
</template>
<script>
import { createNamespacedHelpers } from 'vuex'

import FieldLabel from '@/components/FieldLabel.vue'
import RadioOptions from '@/components/RadioOptions.vue'
import TextArea from '@/components/TextArea.vue'
import BasePhase from './BasePhase.vue'

import {
  FAMILY_HISTORY_FAMILY_OPTIONS,
  FAMILY_HISTORY_PROFILE_OPTIONS,
  FAMILY_ATOPIA_OPTIONS,
  FAMILY_ATOPIA_SUMMARIZED_OPTIONS_PSORIASIS,
  FAMILY_ATOPIA_SUMMARIZED_OPTIONS_DA,
  FAMILY_HISTORY_SUMMARIZED_OPTIONS_PSORIASIS,
} from '@/components/AttendanceForm/general/constants/familyHistory'
import { DISEASES } from '@/constants/disease'

const { mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')

export default {
  components: { FieldLabel, TextArea, RadioOptions },
  extends: BasePhase,
  computed: {
    ...attendanceGettersMap(['attendanceDiseaseId']),
    ...attendanceGettersMap(['attendance']),
    defaultProfileOptions() {
      return [
        { text: 'Sim', value: true },
        { text: 'Não', value: false },
      ]
    },
    profileOptions() {
      if (this.attendanceDiseaseId === DISEASES.PSORIASIS && this.attendance.data.summarizedFlow) {
        return FAMILY_HISTORY_SUMMARIZED_OPTIONS_PSORIASIS
      }
      if (this.attendanceDiseaseId === DISEASES.ATOPIC_DERMATITIS && this.attendance.data.summarizedFlow) {
        return []
      } else {
        return FAMILY_HISTORY_PROFILE_OPTIONS.filter(
          (option) => !this.attendance.data.summarizedFlow || option.required,
        )
      }
    },

    familyAtopiaOptions() {
      if (this.attendanceDiseaseId === DISEASES.PSORIASIS && this.attendance.data.summarizedFlow) {
        return FAMILY_ATOPIA_SUMMARIZED_OPTIONS_PSORIASIS
      }
      if (this.attendanceDiseaseId === DISEASES.ATOPIC_DERMATITIS && this.attendance.data.summarizedFlow) {
        return FAMILY_ATOPIA_SUMMARIZED_OPTIONS_DA
      } else {
        return FAMILY_ATOPIA_OPTIONS.filter((option) => !this.attendance.data.summarizedFlow || option.required)
      }
    },
  },
  data() {
    return {
      isMounted: false,
      familyOptions: FAMILY_HISTORY_FAMILY_OPTIONS,
    }
  },
  methods: {
    getDefaultData() {
      const data = {
        familyAtopias: this.buildFamilyAtopia(),
        profile: this.buildEmptyProfile(),
        notes: null,
      }
      return data
    },
    buildFamilyAtopia() {
      return {
        asma: {
          selected: null,
          degree: [],
        },
        rinite_alergica: {
          selected: null,
          degree: [],
        },
        dermatite_atopica: {
          selected: null,
          degree: [],
        },
        psoriase: {
          selected: null,
          degree: [],
        },
        angioedema: {
          selected: null,
          degree: [],
        },
        urticaria: {
          selected: null,
          degree: [],
        },
      }
    },
    buildEmptyProfile() {
      return FAMILY_HISTORY_PROFILE_OPTIONS.reduce(
        (acc, option) => ({ ...acc, [option.key]: { value: null, complement: null, treatment: null, degree: [] } }),
        {},
      )
    },
    buildDegreeInProfile() {
      return FAMILY_HISTORY_PROFILE_OPTIONS.reduce(
        (acc, option) => ({
          ...acc,
          [option.key]: {
            value: this.phaseData.profile[option.key]?.value,
            complement: this.phaseData.profile[option.key]?.complement,
            treatment: this.phaseData.profile[option.key]?.treatment,
            degree: this.phaseData.profile[option.key]?.degree ?? [],
          },
        }),
        {},
      )
    },
    onNoFamilyWithPsoriasisChange() {
      this.phaseData.familyWithPsoriasis = []
    },
    checkProfile(key, value) {
      if (!value) {
        if (key === 'doenca_reumatologica' || key === 'cancer') {
          this.phaseData.profile[key].complement = null
        }
      }
      if (!this.phaseData.profile[key].value) {
        this.phaseData.profile[key].complement = null
      }
    },
    resetObject(key) {
      if (!this.phaseData.familyAtopias[key].selected) {
        this.phaseData.familyAtopias[key].degree = []
      }
    },
  },
  mounted() {
    if (!this.phaseData?.familyAtopias) this.phaseData.familyAtopias = this.buildFamilyAtopia()
    if (!this.phaseData?.familyAtopias?.urticaria || !this.phaseData?.familyAtopias?.angioedema) {
      this.phaseData.familyAtopias.urticaria = {
        selected: null,
        degree: [],
      }
      this.phaseData.familyAtopias.angioedema = {
        selected: null,
        degree: [],
      }
    }
    if (!this.phaseData.profile.doenca_reumatologia?.degree || !this.phaseData.profile.doenca_reumatologia?.treatment)
      this.phaseData.profile = this.buildDegreeInProfile()
    this.isMounted = true
  },
}
</script>

<style scoped></style>
